/**
 * Dove punta il client Limulo, e con quale materiale d'identita' in sviluppo.
 *
 * Qui ci sono le REGOLE; i valori li legge `src/env.ts`, che chiama queste funzioni. La
 * separazione non e' formale: sotto Node `import.meta.env` non esiste, quindi una sola
 * lettura qui renderebbe inesercitabile l'intero modulo — e la modifica a `env.ts`, che e'
 * un chokepoint condiviso con altri cantieri, si riduce cosi' a tre righe invece di
 * ricalcolare la stessa logica in un secondo posto.
 */

/**
 * ⚠️ IL PREFISSO DI DEV NON PUO' CHIAMARSI `/qlash-api`, e la ragione va scritta perche'
 * e' controintuitiva: `/qlash-api` e' contemporaneamente
 *
 *   (a) il path pubblico VERO del BFF Limulo — `manifest.js routes[].path = 'qlash-api'`;
 *       misurato il 2026-08-30, `POST https://staging-api.qlash.gg/qlash-api/v1/auth/
 *       exchange-token` con corpo `{}` risponde 400 `externalToken richiesto`;
 *   (b) il prefisso proxy che QUESTA app usa gia' per il monolite Laravel
 *       (`env.ts:25` → `apiUrl: '/qlash-api'`, `vite.config.ts:135`).
 *
 * Chi «usa il nome giusto» qui dentro dirotta ogni chiamata Laravel della TMA dentro
 * Limulo. Il proxy di dev prende quindi un nome suo e il rewrite in `vite.config.ts`
 * rimette il path vero verso l'origine.
 */
export const LIMULO_DEV_PROXY_PREFIX = '/limulo-api';

/** Bersaglio unico e dichiarato: staging. Nessun ramo di produzione, in fase 1. */
export const LIMULO_STAGING_ORIGIN = 'https://staging-api.qlash.gg';

/** Il path su cui il BFF e' montato dall'host (`manifest.js routes[].path`). */
export const LIMULO_MOUNT_PATH = '/qlash-api';

export function resolveLimuloApiUrl(isDev: boolean, override: string | undefined): string {
  if (override) return override;
  return isDev ? LIMULO_DEV_PROXY_PREFIX : `${LIMULO_STAGING_ORIGIN}${LIMULO_MOUNT_PATH}`;
}

/**
 * ⚠️ QUI NON C'E' — E NON DEVE TORNARE — UNA `resolveDevExternalToken(isDev, raw)`.
 *
 * C'era, il 2026-08-30, e sembrava un miglioramento: rispettava la regola del repo
 * (`isDev ? … : ''`), era testabile, ed era piu' leggibile del ternario ripetuto in
 * `env.ts`. Era invece una regressione di sicurezza, e la prova e' nel bundle.
 *
 * La garanzia della forma `isDev ? orDefault(import.meta.env.VITE_X, '') : ''` non sta nel
 * VALORE che restituisce: sta nel fatto che il bundler la piega a compile-time — `isDev` e'
 * una costante nota, il ramo vero viene eliminato, e il valore della variabile non compare
 * nel JavaScript prodotto. Una CHIAMATA di funzione non e' piegabile: il bundler ne
 * conserva l'argomento. Il token non arriva mai alla variabile a runtime, ma la stringa
 * resta scritta nel file servito, leggibile da chiunque apra i sorgenti.
 *
 * Misurato: build con `VITE_LIMULO_DEV_EXTERNAL_TOKEN` impostato a una sentinella, e la
 * sentinella ritrovata dentro `dist/assets/*.js` accanto a `limuloDevExternalToken:`.
 *
 * Da qui la divisione di questo file: le regole che si possono provare stanno qui, ma
 * quella del token NON e' una regola provabile in isolamento — e' una proprieta' del
 * BUNDLE, e si difende con la forma del codice in `env.ts` piu' il controllo testuale in
 * `tools/verify-limulo.ts`.
 *
 * La distinzione non e' «funzioni si' / funzioni no»: `resolveLimuloApiUrl` qui sopra e'
 * una funzione e va benissimo, perche' un URL pubblico finirebbe nel bundle comunque. La
 * domanda e' sempre la stessa: questo valore puo' stare nel file servito?
 */

// ⚠️ NIENTE `import.meta.env` in questo file, e non e' un dettaglio di gusto: sotto Node
// `import.meta.env` e' `undefined`, quindi una sola lettura qui renderebbe INESERCITABILE
// l'intero modulo — e con lui le regole che decidono dove punta il client. La lettura
// delle variabili vive dove il repo dice che deve vivere, `src/env.ts`, che chiama queste
// funzioni. Cosi' il valore ha una sorgente sola e le regole restano provabili.
